// Import Orval-generated clients
import {
    useGetCustomerProfile1,
    useCreateProfile,
    useUpdateProfileWithOtp,
    useSendTermsAndConditions as useSendTermsAndConditionsApi,
    useSendOtpForPaymentInfoChange as useSendOtpForPayment,
    useAcceptTermsAndConditions as useAcceptTermsAndConditionsApi
} from '@/shared/api/client/customer-profile';
import { useGetFspsByType as useGetFspsByType1 } from '@/shared/api/client/fsp';
import { AxiosResponse } from 'axios';
import {CustomerProfileDTO, FspDto, FspType} from "@/shared/api/model";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { customInstance } from '@/shared/api/mutator/custom-instance';

export interface DeductionStatusDTO {
    deductionFormRequired?: boolean;
    deductionStatus?: string;
    monthlyDeductionAmount?: number;
    officialDeductionFormRef?: string;
    officialDeductionFormUploadedAt?: string;
    signedDeductionFormUploaded?: boolean;
    signedDeductionFormRef?: string;
    signedDeductionFormUploadedAt?: string;
    selfRegistrationSessionToken?: string;
}

export function useDeductionStatus() {
    return useQuery<DeductionStatusDTO>({
        queryKey: ['deduction-status'],
        queryFn: async () => {
            const response = await customInstance<DeductionStatusDTO>({
                url: '/api/frontend/admin/customer-profile/deduction-status',
                method: 'GET'
            });
            return (response as any)?.data ?? response;
        },
        staleTime: 2 * 60 * 1000
    });
}

export function useCustomerProfile() {
    const result = useGetCustomerProfile1();

    return [{
        isLoading: result.isLoading,
        isError: result.isError,
        data: result.data
    }];
}

export function useCreateCustomerProfile() {
    const result = useCreateProfile();

    return {
        isPending: result.isPending,
        isError: result.isError,
        mutate: (customerProfile: CustomerProfileDTO) =>
            result.mutate({ data: customerProfile }),
        mutateAsync: (customerProfile: CustomerProfileDTO) =>
            result.mutateAsync({ data: customerProfile })
    };
}

export const useAcceptTermsAndConditions = () => {
    const result = useAcceptTermsAndConditionsApi();

    return {
        mutationFn: ({ otp }: { otp: string }) =>
            result.mutate({ params: { otp } }),
        mutate: ({ otp }: { otp: string }) =>
            result.mutate({ params: { otp } }),
        isPending: result.isPending,
        isError: result.isError
    };
};

export function useUpdateCustomerProfileWithOtp() {
    const result = useUpdateProfileWithOtp();

    return {
        isPending: result.isPending,
        isError: result.isError,
        mutate: (customerProfile: CustomerProfileDTO) =>
            result.mutate({ data: customerProfile }),
        mutateAsync: (customerProfile: CustomerProfileDTO) =>
            result.mutateAsync({ data: customerProfile })
    };
}

export const useSendTermsAndConditions = () => {
    const result = useSendTermsAndConditionsApi();

    return {
        mutationFn: () => result.mutate(),
        mutate: () => result.mutate(),
        isPending: result.isPending,
        isError: result.isError
    };
};

export function useSendOtpForPaymentInfoChange() {
    const result = useSendOtpForPayment();

    return {
        isPending: result.isPending,
        isError: result.isError,
        mutate: () => result.mutate(),
        mutateAsync: () => result.mutateAsync()
    };
}

export function useGetFspsByType(type: FspType): [UseQueryResult<FspDto[], Error>, () => void] {
    const query = useGetFspsByType1(type, {
        query: {
            queryKey: ['fsps', type],
            enabled: !!type // Only run the query if type is provided
        }
    });

    const refetch = () => {
        query.refetch();
    };

    // Extract the data from the Axios response and properly type it
    const adaptedQuery = {
        ...query,
        data: query.data
    };

    return [
        adaptedQuery as unknown as UseQueryResult<FspDto[], Error>,
        refetch
    ];
}